import { Point } from '@/features/canvas/types';
import { connectorGeometry } from './geometry';
import type { Draft } from './overlay-svg';

const SELECT = '#2ec5e8'; // accent

/** A small filled arrowhead at `tip`, pointing along the incoming direction from `from`. */
function arrowHead(tip: Point, from: Point, color: string, key: string) {
  const ang = Math.atan2(tip.y - from.y, tip.x - from.x);
  const len = 12;
  const spread = 0.4;
  const a = { x: tip.x - len * Math.cos(ang - spread), y: tip.y - len * Math.sin(ang - spread) };
  const b = { x: tip.x - len * Math.cos(ang + spread), y: tip.y - len * Math.sin(ang + spread) };
  return <path key={key} d={`M ${tip.x} ${tip.y} L ${a.x} ${a.y} L ${b.x} ${b.y} Z`} fill={color} />;
}

/**
 * The in-progress non-ink gesture preview (connector rubber-band, drag-to-create rect), drawn in world
 * space on its own svg. The pen draft is rendered on the GPU (PixiInkLayer) so the live stroke matches
 * the committed ink exactly.
 */
export default function DraftLayer({ draft }: { draft: Draft }) {
  if (!draft || draft.kind === 'pen') return null;
  return (
    <svg style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 1, overflow: 'visible', pointerEvents: 'none' }}>
      {draft.kind === 'connector'
        ? (() => {
            // Preview the ACTUAL connector that will be created — same 'curved' routing + arrowhead,
            // attaching to the source edge (the nub/border you grabbed) and the target's edge (or the free
            // cursor) — rather than a plain straight line.
            const toBox = draft.toBox ?? { x: draft.to.x, y: draft.to.y, w: 0, h: 0 };
            const g = connectorGeometry(draft.from, toBox, {
              connectorType: 'curved',
              from_anchor: draft.fromAnchor,
              to_anchor: draft.toAnchor,
            });
            return (
              <g>
                <path d={g.d} fill="none" stroke={draft.color} strokeWidth={2} strokeDasharray="4 4" strokeLinecap="round" strokeLinejoin="round" />
                {arrowHead(g.end, g.endFrom, draft.color, 'draft-ah')}
              </g>
            );
          })()
        : null}

      {draft.kind === 'create' ? (
        <rect x={draft.rect.x} y={draft.rect.y} width={draft.rect.w} height={draft.rect.h} fill={SELECT} fillOpacity={0.08} stroke={SELECT} strokeWidth={1} strokeDasharray="4 4" />
      ) : null}
    </svg>
  );
}
